import { useMemo, useState } from 'react';
import { Users, MessageSquare, Zap, Activity, Bot, BarChart3, CalendarDays, DollarSign } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Skeleton } from '@/components/ui/skeleton';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  CartesianGrid,
} from 'recharts';

type PeriodPreset = '7d' | '30d' | 'month' | 'custom';

interface UsageSummary {
  total_users: number;
  active_users: number;
  total_conversations: number;
  total_messages: number;
  total_tokens: number;
  input_tokens: number;
  output_tokens: number;
  cached_input_tokens: number;
  estimated_cost_usd: number;
}

interface DailyStat {
  day: string;
  total_messages: number;
  total_tokens: number;
  input_tokens: number;
  output_tokens: number;
  estimated_cost_usd: number;
  unique_users: number;
}

interface UserActivity {
  user_id: string;
  email: string;
  nome: string | null;
  avatar_url: string | null;
  role: string;
  total_conversations: number;
  total_messages: number;
  total_tokens: number;
  last_active: string | null;
}

interface AgentUsage {
  agent_id: string;
  agent_name: string;
  scope_type: string;
  model: string | null;
  model_name: string | null;
  total_conversations: number;
  total_messages: number;
  total_tokens: number;
  input_tokens: number;
  output_tokens: number;
  cached_input_tokens: number;
  estimated_cost_usd: number;
  last_active: string | null;
}

interface ModelUsage {
  model: string;
  model_name: string;
  provider: string;
  total_conversations: number;
  total_messages: number;
  total_tokens: number;
  input_tokens: number;
  output_tokens: number;
  cached_input_tokens: number;
  estimated_cost_usd: number;
  prompt_price_per_1m: number | null;
  completion_price_per_1m: number | null;
  last_active: string | null;
}

const formatNumber = (n: number | null | undefined) => Number(n ?? 0).toLocaleString('pt-BR');
const formatCurrency = (n: number | null | undefined) =>
  Number(n ?? 0).toLocaleString('pt-BR', { style: 'currency', currency: 'USD', minimumFractionDigits: 4, maximumFractionDigits: 4 });
const formatPrice = (n: number | null | undefined) => (n == null ? '—' : formatCurrency(n));

const SCOPE_BADGE: Record<string, { label: string; className: string }> = {
  global: { label: 'Global', className: 'bg-blue-500/15 text-blue-600 border-blue-500/30' },
  team: { label: 'Time', className: 'bg-green-500/15 text-green-600 border-green-500/30' },
  personal: { label: 'Pessoal', className: 'bg-purple-500/15 text-purple-500 border-purple-500/30' },
};

const formatDay = (dateStr: string) => {
  const d = new Date(dateStr);
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const toDateInput = (date: Date) => date.toISOString().slice(0, 10);
const endOfDateInput = (dateStr: string) => `${dateStr}T23:59:59.999Z`;

function getPeriodRange(preset: PeriodPreset, customStart: string, customEnd: string) {
  const now = new Date();
  const start = new Date(now);

  if (preset === '7d') start.setDate(now.getDate() - 7);
  if (preset === '30d') start.setDate(now.getDate() - 30);
  if (preset === 'month') start.setDate(1);

  if (preset === 'custom') {
    return {
      start: customStart ? new Date(`${customStart}T00:00:00.000Z`).toISOString() : new Date(now.getTime() - 30 * 86400000).toISOString(),
      end: customEnd ? new Date(endOfDateInput(customEnd)).toISOString() : now.toISOString(),
    };
  }

  return { start: start.toISOString(), end: now.toISOString() };
}

const presetLabels: Record<PeriodPreset, string> = {
  '7d': '7 dias',
  '30d': '30 dias',
  month: 'Mês atual',
  custom: 'Customizado',
};

export const MetricsDashboard = () => {
  const [period, setPeriod] = useState<PeriodPreset>('30d');
  const [customStart, setCustomStart] = useState(toDateInput(new Date(Date.now() - 30 * 86400000)));
  const [customEnd, setCustomEnd] = useState(toDateInput(new Date()));

  const range = useMemo(() => getPeriodRange(period, customStart, customEnd), [period, customStart, customEnd]);
  const rpcParams = { p_start: range.start, p_end: range.end };

  const { data: summary, isLoading: summaryLoading } = useQuery<UsageSummary | null>({
    queryKey: ['admin-usage-summary', range.start, range.end],
    queryFn: async () => {
      const { data, error } = await (supabase as any).rpc('get_usage_summary', rpcParams);
      if (error) throw error;
      return (data as UsageSummary[])?.[0] ?? null;
    },
  });

  const { data: dailyStats = [], isLoading: dailyLoading } = useQuery<DailyStat[]>({
    queryKey: ['admin-daily-usage-stats', range.start, range.end],
    queryFn: async () => {
      const { data, error } = await (supabase as any).rpc('get_daily_usage_stats', rpcParams);
      if (error) throw error;
      return (data as DailyStat[]) ?? [];
    },
  });

  const { data: userActivity = [], isLoading: activityLoading } = useQuery<UserActivity[]>({
    queryKey: ['admin-user-activity', range.start, range.end],
    queryFn: async () => {
      const { data, error } = await (supabase as any).rpc('get_user_activity_stats', rpcParams);
      if (error) throw error;
      return ((data as UserActivity[]) ?? []).slice(0, 10);
    },
  });

  const { data: agentUsage = [], isLoading: agentLoading } = useQuery<AgentUsage[]>({
    queryKey: ['admin-agent-usage', range.start, range.end],
    queryFn: async () => {
      const { data, error } = await (supabase as any).rpc('get_agent_usage_stats', rpcParams);
      if (error) throw error;
      return ((data as AgentUsage[]) ?? []).slice(0, 10);
    },
  });

  const { data: modelUsage = [], isLoading: modelLoading } = useQuery<ModelUsage[]>({
    queryKey: ['admin-model-usage', range.start, range.end],
    queryFn: async () => {
      const { data, error } = await (supabase as any).rpc('get_model_usage_stats', rpcParams);
      if (error) throw error;
      return ((data as ModelUsage[]) ?? []).slice(0, 10);
    },
  });

  const chartData = useMemo(
    () => [...dailyStats].reverse().map((d) => ({ ...d, label: formatDay(d.day) })),
    [dailyStats]
  );

  const statCards = [
    { key: 'active_users' as const, label: 'Usuários Ativos', icon: Users, color: 'text-blue-500' },
    { key: 'total_messages' as const, label: 'Mensagens', icon: MessageSquare, color: 'text-green-500' },
    { key: 'total_tokens' as const, label: 'Tokens', icon: Zap, color: 'text-amber-500' },
    { key: 'estimated_cost_usd' as const, label: 'Custo Estimado', icon: DollarSign, color: 'text-purple-500', currency: true },
  ];

  return (
    <div className="space-y-8">
      <div className="bg-surface border border-color rounded-xl p-5 space-y-4">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-center gap-2">
            <CalendarDays className="h-4 w-4 text-secondary" />
            <h3 className="text-sm font-semibold text-primary">Período das métricas</h3>
          </div>
          <div className="flex flex-wrap gap-2">
            {(['7d', '30d', 'month'] as PeriodPreset[]).map((preset) => (
              <Button key={preset} type="button" size="sm" variant={period === preset ? 'default' : 'outline'} onClick={() => setPeriod(preset)}>
                {presetLabels[preset]}
              </Button>
            ))}
            <Button type="button" size="sm" variant={period === 'custom' ? 'default' : 'outline'} onClick={() => setPeriod('custom')}>
              Customizado
            </Button>
          </div>
        </div>
        {period === 'custom' && (
          <div className="grid gap-3 sm:grid-cols-2 lg:max-w-xl">
            <Input type="date" value={customStart} onChange={(e) => setCustomStart(e.target.value)} />
            <Input type="date" value={customEnd} onChange={(e) => setCustomEnd(e.target.value)} />
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((card) => (
          <div key={card.key} className="bg-surface border border-color rounded-xl p-5 flex flex-col gap-2">
            <card.icon className={`h-5 w-5 ${card.color}`} />
            {summaryLoading ? (
              <Skeleton className="h-8 w-20" />
            ) : (
              <span className="text-2xl font-bold text-primary">
                {card.currency ? formatCurrency(summary?.[card.key] as number) : formatNumber(summary?.[card.key] as number)}
              </span>
            )}
            <span className="text-xs text-secondary">{card.label}</span>
          </div>
        ))}
      </div>

      <div className="bg-surface border border-color rounded-xl p-6">
        <h3 className="text-sm font-semibold text-primary mb-4">Consumo diário ({presetLabels[period]})</h3>
        {dailyLoading ? (
          <Skeleton className="h-64 w-full" />
        ) : chartData.length === 0 ? (
          <div className="flex items-center justify-center py-16 text-secondary text-sm">Nenhum dado de uso registrado no período.</div>
        ) : (
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
              <YAxis tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
              <RechartsTooltip
                contentStyle={{ backgroundColor: 'hsl(var(--popover))', border: '1px solid hsl(var(--border))', borderRadius: 8, fontSize: 12 }}
                labelStyle={{ color: 'hsl(var(--popover-foreground))' }}
                formatter={(value: number, name: string) => [formatNumber(value), name === 'total_tokens' ? 'Tokens' : 'Mensagens']}
                labelFormatter={(label) => `Dia: ${label}`}
              />
              <Bar dataKey="total_tokens" fill="hsl(var(--brand-primary))" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      <div className="bg-surface border border-color rounded-xl overflow-hidden">
        <div className="px-6 py-4 border-b border-color flex items-center gap-2">
          <Bot className="h-4 w-4 text-secondary" />
          <h3 className="text-sm font-semibold text-primary">Agentes que Mais Consomem</h3>
        </div>
        {agentLoading ? (
          <div className="p-6 space-y-3">{Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}</div>
        ) : agentUsage.length === 0 ? (
          <div className="flex items-center justify-center py-16 text-secondary text-sm">Nenhum uso de agente registrado no período.</div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="bg-secondary">
                <TableHead>Agente</TableHead>
                <TableHead>Escopo</TableHead>
                <TableHead>Modelo</TableHead>
                <TableHead className="text-right">Mensagens</TableHead>
                <TableHead className="text-right">Tokens</TableHead>
                <TableHead className="text-right">Custo</TableHead>
                <TableHead>Última atividade</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {agentUsage.map((a) => {
                const badge = SCOPE_BADGE[a.scope_type] ?? SCOPE_BADGE.personal;
                return (
                  <TableRow key={a.agent_id}>
                    <TableCell className="font-medium text-primary text-sm">{a.agent_name || '—'}</TableCell>
                    <TableCell><Badge variant="outline" className={badge.className}>{badge.label}</Badge></TableCell>
                    <TableCell className="text-secondary text-xs max-w-56 truncate">{a.model_name || a.model || '—'}</TableCell>
                    <TableCell className="text-right text-sm text-primary font-medium">{formatNumber(a.total_messages)}</TableCell>
                    <TableCell className="text-right text-sm text-primary font-medium">{formatNumber(a.total_tokens)}</TableCell>
                    <TableCell className="text-right text-sm text-primary font-medium">{formatCurrency(a.estimated_cost_usd)}</TableCell>
                    <TableCell className="text-secondary text-sm">{a.last_active ? new Date(a.last_active).toLocaleDateString('pt-BR') : '—'}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </div>

      <div className="bg-surface border border-color rounded-xl overflow-hidden">
        <div className="px-6 py-4 border-b border-color flex items-center gap-2">
          <BarChart3 className="h-4 w-4 text-secondary" />
          <h3 className="text-sm font-semibold text-primary">Modelos LLM por Consumo</h3>
        </div>
        {modelLoading ? (
          <div className="p-6 space-y-3">{Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}</div>
        ) : modelUsage.length === 0 ? (
          <div className="flex items-center justify-center py-16 text-secondary text-sm">Nenhum uso de modelo registrado no período.</div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="bg-secondary">
                <TableHead>Modelo</TableHead>
                <TableHead>Provider</TableHead>
                <TableHead className="text-right">Conversas</TableHead>
                <TableHead className="text-right">Input</TableHead>
                <TableHead className="text-right">Output</TableHead>
                <TableHead className="text-right">Total</TableHead>
                <TableHead className="text-right">Custo</TableHead>
                <TableHead className="text-right">$/1M in</TableHead>
                <TableHead className="text-right">$/1M out</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {modelUsage.map((m) => (
                <TableRow key={m.model}>
                  <TableCell className="font-medium text-primary text-sm max-w-64 truncate">{m.model_name || m.model}</TableCell>
                  <TableCell className="text-secondary text-sm">{m.provider}</TableCell>
                  <TableCell className="text-right text-sm text-primary font-medium">{formatNumber(m.total_conversations)}</TableCell>
                  <TableCell className="text-right text-sm text-primary font-medium">{formatNumber(m.input_tokens)}</TableCell>
                  <TableCell className="text-right text-sm text-primary font-medium">{formatNumber(m.output_tokens)}</TableCell>
                  <TableCell className="text-right text-sm text-primary font-medium">{formatNumber(m.total_tokens)}</TableCell>
                  <TableCell className="text-right text-sm text-primary font-medium">{formatCurrency(m.estimated_cost_usd)}</TableCell>
                  <TableCell className="text-right text-sm text-secondary">{formatPrice(m.prompt_price_per_1m)}</TableCell>
                  <TableCell className="text-right text-sm text-secondary">{formatPrice(m.completion_price_per_1m)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <div className="bg-surface border border-color rounded-xl overflow-hidden">
        <div className="px-6 py-4 border-b border-color">
          <h3 className="text-sm font-semibold text-primary">Usuários mais ativos</h3>
        </div>
        {activityLoading ? (
          <div className="p-6 space-y-3">{Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}</div>
        ) : userActivity.length === 0 ? (
          <div className="flex items-center justify-center py-16 text-secondary text-sm">Nenhum dado de atividade registrado no período.</div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="bg-secondary">
                <TableHead>Usuário</TableHead>
                <TableHead>Email</TableHead>
                <TableHead className="text-right">Mensagens</TableHead>
                <TableHead className="text-right">Tokens</TableHead>
                <TableHead>Última atividade</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {userActivity.map((u) => {
                const initials = (u.nome || u.email).split(' ').map((n) => n[0]).slice(0, 2).join('').toUpperCase();
                return (
                  <TableRow key={u.user_id}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Avatar className="h-7 w-7">
                          {u.avatar_url && <AvatarImage src={u.avatar_url} alt={u.nome || u.email} />}
                          <AvatarFallback className="text-[10px] bg-secondary text-primary">{initials}</AvatarFallback>
                        </Avatar>
                        <span className="font-medium text-primary text-sm">{u.nome || '—'}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-secondary text-sm">{u.email}</TableCell>
                    <TableCell className="text-right text-sm text-primary font-medium">{formatNumber(u.total_messages)}</TableCell>
                    <TableCell className="text-right text-sm text-primary font-medium">{formatNumber(u.total_tokens)}</TableCell>
                    <TableCell className="text-secondary text-sm">{u.last_active ? new Date(u.last_active).toLocaleDateString('pt-BR') : '—'}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
};
